import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { rideAPI, uploadAPI } from "../services/api";
import { useGPS } from "../hooks/useGPS";
import { useOffline } from "../hooks/useOffline";
import toast from "react-hot-toast";
import Header from "./Header";
import RideDetailsCard from "./RideDetailsCard";
import DriverDetailsCard from "./DriverDetailsCard";
import VehicleDetailsCard from "./VehicleDetailsCard";
import StatusFlow from "./StatusFlow";
import FinalPhotoCapture from "./FinalPhotoCapture";
import OfflineIndicator from "./OfflineIndicator";
import LoadingSpinner from "./LoadingSpinner";

const DriverApp = () => {
  const { rideId } = useParams();
  const location = useLocation();
  const recordId = useMemo(() => {
    // Support link format: /{ride_id}&{record_id}
    const seg = location.pathname.replace(/^\//, "");
    const [idPart, recPart] = seg.split("&");
    return recPart || new URLSearchParams(location.search).get("record_id");
  }, [location.pathname, location.search]);

  const pureRideId = useMemo(() => {
    const seg = location.pathname.replace(/^\//, "");
    const [idPart] = seg.split("&");
    return idPart;
  }, [location.pathname]);

  const [rideData, setRideData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [driverInfo, setDriverInfo] = useState({});
  const [vehiclePhotoUrl, setVehiclePhotoUrl] = useState(null);
  const [driverSelfieUrl, setDriverSelfieUrl] = useState(null);
  const [driverInfoLocked, setDriverInfoLocked] = useState(false);
  const [vehicleInfoLocked, setVehicleInfoLocked] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null); // Accordion state
  const [showFinalPhotoCapture, setShowFinalPhotoCapture] = useState(false);
  const [isSubmittingFinalPhotos, setIsSubmittingFinalPhotos] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const { captureGPS, isLoading: gpsLoading } = useGPS();
  const { isOnline, pendingActions, savePendingAction, removePendingAction } =
    useOffline();

  // Load ride data on mount
  useEffect(() => {
    loadRideData();
  }, [pureRideId, recordId]);

  // Process pending actions when online
  useEffect(() => {
    if (isOnline && pendingActions.length > 0) {
      processPendingActions();
    }
  }, [isOnline, pendingActions]);

  const applyRideData = (data) => {
    setRideData(data);

    // Set driver info from new API fields
    if (data.driver_status?.driver_info) {
      setDriverInfo(data.driver_status.driver_info);
    }

    // Set selfie URL from new API field
    const selfieUrl =
      data.driver_status?.driver_info?.driver_selfie_url ||
      data.driver_status?.driver_info?.["Driver selfie"];
    if (selfieUrl) setDriverSelfieUrl(selfieUrl);

    // Set vehicle info from new API field
    if (data.driver_status?.driver_vehicle_info) {
      setDriverInfo((prev) => ({
        ...prev,
        ...data.driver_status.driver_vehicle_info,
      }));
    }

    // Set vehicle photo URL from new API field
    const vehiclePhotoUrlApi =
      data.driver_status?.driver_vehicle_info?.vehicle_photo_url ||
      data.driver_status?.driver_vehicle_info?.["Vehicle Photo"];
    if (vehiclePhotoUrlApi) setVehiclePhotoUrl(vehiclePhotoUrlApi);

    // Lock driver/vehicle info if already submitted
    setDriverInfoLocked(!!data.driver_status?.driver_info_complete);
    setVehicleInfoLocked(!!data.driver_status?.vehicle_info_complete);
  };

  // POST responses carry the updated ride; only reload when they don't
  const refreshFromResponse = (response) =>
    response?.data ? applyRideData(response.data) : loadRideData();

  const loadRideData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await rideAPI.getRideDetails(pureRideId, recordId);

      if (response.success) {
        applyRideData(response.data);
      } else {
        throw new Error(response.message || "Failed to load ride data");
      }
    } catch (err) {
      console.error("Failed to load ride data:", err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processPendingActions = async () => {
    for (const action of pendingActions) {
      try {
        if (action.type === "status_update") {
          const response = await rideAPI.updateStatus(
            pureRideId,
            recordId,
            action.data
          );
          removePendingAction(action.id);
          toast.success("Pending action completed");
          refreshFromResponse(response);
        }
      } catch (err) {
        console.error("Failed to process pending action:", err);
        toast.error("Failed to process pending action");
      }
    }
  };

  const uploadPhotoToGCS = async (file, rideIdNum) => {
    try {
      // Validate file type
      if (
        !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
          file.type
        )
      ) {
        throw new Error("File must be JPEG, PNG, or WebP format");
      }

      return await uploadAPI.uploadPhoto(file, rideIdNum);
    } catch (err) {
      console.error("Photo upload failed:", err);
      throw err;
    }
  };

  // Uploaded drop photos by File, so a retry (e.g. after a GPS failure)
  // doesn't upload the same photo again
  const uploadedDropPhotos = useRef(new WeakMap());

  const uploadDropPhoto = async (file) => {
    const cached = uploadedDropPhotos.current.get(file);
    if (cached) return cached;
    const url = await uploadPhotoToGCS(file, Number(pureRideId));
    uploadedDropPhotos.current.set(file, url);
    return url;
  };

  // Throws on failure so the photo modal stays open with the photos kept
  const handleFinalPhotosCapture = async (photoFiles, notes = "") => {
    if (photoFiles.length === 0) {
      toast.error("Please add at least one drop-off photo.");
      throw new Error("Drop-off photo required");
    }
    if (photoFiles.some((f) => !["image/jpeg", "image/jpg", "image/png"].includes(f.type))) {
      toast.error("Please select JPG, JPEG, or PNG images only.");
      throw new Error("Invalid photo type");
    }

    try {
      setIsSubmittingFinalPhotos(true);

      // GPS and the photo uploads run at the same time
      const [gps, uploadedUrls] = await Promise.all([
        captureGPS(),
        Promise.all(photoFiles.map(uploadDropPhoto)),
      ]);
      const gpsString = `${gps.lat},${gps.lng},${gps.accuracy}`;

      const statusData = {
        status: "passenger_drop_successful",
        gps: gpsString,
      };

      // Include photo_url only if photos were uploaded
      if (uploadedUrls.length > 0) {
        statusData.photo_url =
          uploadedUrls.length === 1 ? uploadedUrls[0] : uploadedUrls;
      }

      // Add notes only if provided
      if (notes && notes.trim()) {
        statusData.notes = notes.trim();
      }

      if (isOnline) {
        const response = await rideAPI.updateStatus(
          pureRideId,
          recordId,
          statusData
        );

        if (response.success) {
          toast.success("Ride completed!");
          refreshFromResponse(response);
          setShowFinalPhotoCapture(false);
        }
      } else {
        savePendingAction({
          type: "status_update",
          data: statusData,
        });
        setShowFinalPhotoCapture(false);
        toast.success("Ride completed. Will sync when online.");
      }
    } catch (err) {
      console.error("Failed to submit completion photos:", err);
      // useGPS already showed its own message
      if (!err.toastShown) toast.error(err.message || "Failed to submit photos");
      throw err;
    } finally {
      setIsSubmittingFinalPhotos(false);
    }
  };

  const handleStatusUpdate = async (status, notes = "") => {
    if (status === "passenger_drop_successful") {
      // Open photo capture modal instead
      setShowFinalPhotoCapture(true);
      return;
    }

    try {
      setIsUpdatingStatus(true);
      // Capture GPS first
      const gps = await captureGPS();
      const gpsString = `${gps.lat},${gps.lng},${gps.accuracy}`;

      const statusData = {
        status,
        gps: gpsString,
      };

      // Add notes only if provided
      if (notes && notes.trim()) {
        statusData.notes = notes.trim();
      }

      if (isOnline) {
        const response = await rideAPI.updateStatus(
          pureRideId,
          recordId,
          statusData
        );

        if (response.success) {
          toast.success("Status updated successfully");
          refreshFromResponse(response);
        }
      } else {
        savePendingAction({
          type: "status_update",
          data: statusData,
        });
        toast.success("Status saved. Will sync when online.");
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      if (!err.toastShown) toast.error(err.message);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDriverInfoChange = (newInfo) => {
    setDriverInfo((prev) => ({ ...prev, ...newInfo }));
  };

  const handlePhotoSelect = (file, type) => {
    if (type === "driver_selfie") {
      // Handle driver selfie upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setDriverSelfieUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    } else if (type === "vehicle_photo") {
      // Handle vehicle photo upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setVehiclePhotoUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Accordion toggle function
  const handleCardToggle = (cardName) => {
    setExpandedCard(expandedCard === cardName ? null : cardName);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Unable to load ride
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={loadRideData} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!rideData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Ride not found
          </h1>
          <p className="text-gray-600">
            The requested ride does not exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <OfflineIndicator
        isOnline={isOnline}
        pendingCount={pendingActions.length}
      />

      <div className="max-w-md mx-auto bg-white min-h-screen">
        <Header rideId={pureRideId} />

        <div className="p-4 space-y-4">
          {/* Ride Details Card */}
          <RideDetailsCard
            booking={rideData.booking}
            isExpanded={expandedCard === "ride"}
            onToggle={() => handleCardToggle("ride")}
          />

          {/* Driver Details Card */}
          <DriverDetailsCard
            driverInfo={driverInfo}
            isExpanded={expandedCard === "driver"}
            onToggle={() => handleCardToggle("driver")}
            onDriverInfoChange={handleDriverInfoChange}
            onPhotoSelect={(file) => handlePhotoSelect(file, "driver_selfie")}
            photoUrl={driverSelfieUrl}
            rideId={Number(pureRideId)}
            recordId={recordId}
            locked={driverInfoLocked}
            onSubmitSuccess={(response) => {
              setDriverInfoLocked(true);
              refreshFromResponse(response);
            }}
          />

          {/* Vehicle Details Card */}
          <VehicleDetailsCard
            booking={rideData.booking}
            driverInfo={driverInfo}
            isExpanded={expandedCard === "vehicle"}
            onToggle={() => handleCardToggle("vehicle")}
            onDriverInfoChange={handleDriverInfoChange}
            onPhotoSelect={(file) => handlePhotoSelect(file, "vehicle_photo")}
            vehiclePhotoUrl={vehiclePhotoUrl}
            rideId={Number(pureRideId)}
            recordId={recordId}
            locked={vehicleInfoLocked}
            onSubmitSuccess={(response) => {
              setVehicleInfoLocked(true);
              refreshFromResponse(response);
            }}
          />

          {/* Status Flow */}
          <StatusFlow
            currentStatus={rideData?.driver_status?.current_status}
            onStatusUpdate={handleStatusUpdate}
            isLoading={gpsLoading || isUpdatingStatus}
            onFinalPhotoRequired={() => setShowFinalPhotoCapture(true)}
          />
        </div>
      </div>

      {/* Final Photo Capture Modal */}
      <FinalPhotoCapture
        isOpen={showFinalPhotoCapture}
        onClose={() => setShowFinalPhotoCapture(false)}
        onPhotosCapture={handleFinalPhotosCapture}
        isLoading={isSubmittingFinalPhotos}
      />
    </div>
  );
};

export default DriverApp;
